/* New Time Lock dialog — shared by Vaults, Map wishlist and Level Up nudges. */
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, formatErr } from "@/lib/api";

export default function TimeLockDialog({ onClose, onDone, savingsMin: minProp, savingsMax: maxProp, durations: durProp, initialLabel = "", initialAmount = "", context }) {
  const [cfg, setCfg] = useState({ savingsMin: minProp, savingsMax: maxProp, durations: durProp });
  useEffect(() => {
    if (minProp && maxProp && durProp) return;
    api.get("/config").then(({ data }) => setCfg({
      savingsMin: data.savings_min, savingsMax: data.savings_max,
      durations: data.timelock_durations || data.timelock_duration_presets || [1, 3, 5, 10, 14],
    })).catch(() => setCfg({ savingsMin: 20, savingsMax: 500, durations: [1, 3, 5, 10, 14] }));
  }, [minProp, maxProp, durProp]);
  const savingsMin = cfg.savingsMin ?? 20, savingsMax = cfg.savingsMax ?? 500, durations = cfg.durations || [1, 3, 5, 10, 14];
  const [label, setLabel] = useState(initialLabel);
  const [amount, setAmount] = useState(initialAmount ? String(initialAmount) : "");
  const [mode, setMode] = useState("preset"); // preset | custom
  const [durationDays, setDurationDays] = useState(durations?.[0] || 1);
  const [unlock, setUnlock] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const tomorrow = new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const payload = { label, amount: parseFloat(amount) };
      if (mode === "preset") {
        payload.duration_days = durationDays;
      } else {
        payload.unlock_date = new Date(unlock).toISOString();
      }
      const { data } = await api.post("/timelocks", payload);
      onDone(data);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="timelock-dialog">
      <div className="bg-cream rounded-2xl border border-sand max-w-md w-full p-8">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-forest">New time lock</div>
          <button onClick={onClose} className="text-moss hover:text-ink" data-testid="timelock-close"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-serif text-3xl mt-3">Set a date. Step away.</h3>
        {context && <div className="mt-2 text-xs text-gold" data-testid="timelock-context">{context}</div>}
        <p className="text-sm text-moss mt-3 leading-relaxed">
          Deposit ${savingsMin}–${savingsMax} and pick when you want to see it again.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Label</label>
            <input type="text" required value={label} onChange={(e) => setLabel(e.target.value)}
              className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
              placeholder="Wedding · Sabbatical · Down payment" data-testid="timelock-label" autoFocus />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Amount (USD)</label>
            <input type="number" step="0.01" min={savingsMin} max={savingsMax} required value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono text-lg focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
              placeholder={`${savingsMin}.00`} data-testid="timelock-amount" />
          </div>

          {/* Mode toggle */}
          <div className="flex gap-2 text-xs uppercase tracking-wider font-semibold" data-testid="timelock-mode-toggle">
            <button type="button" onClick={() => setMode("preset")}
              className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                mode === "preset" ? "bg-ink text-white border-ink" : "bg-cream text-moss border-sand hover:border-forest"
              }`} data-testid="timelock-mode-preset">Preset</button>
            <button type="button" onClick={() => setMode("custom")}
              className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                mode === "custom" ? "bg-ink text-white border-ink" : "bg-cream text-moss border-sand hover:border-forest"
              }`} data-testid="timelock-mode-custom">Custom date</button>
          </div>

          {mode === "preset" && (
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Lock for</label>
              <div className="grid grid-cols-4 gap-2 mt-2" data-testid="duration-presets">
                {durations.map((d) => (
                  <button key={d} type="button" onClick={() => setDurationDays(d)}
                    className={`px-3 py-3 rounded-lg border text-sm font-medium transition-all ${
                      durationDays === d
                        ? "bg-forest text-white border-forest shadow-md"
                        : "bg-cream text-ink border-sand hover:border-forest"
                    }`}
                    data-testid={`duration-${d}`}>
                    {d} {d === 1 ? "day" : "days"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === "custom" && (
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Unlock date & time</label>
              <input type="datetime-local" required={mode === "custom"} min={tomorrow}
                value={unlock} onChange={(e) => setUnlock(e.target.value)}
                className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                data-testid="timelock-unlock" />
            </div>
          )}

          {error && <div className="text-sm text-clay" data-testid="timelock-error">{error}</div>}
          <button type="submit" disabled={busy}
            className="w-full bg-forest text-white rounded-lg px-6 py-3.5 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
            data-testid="timelock-submit">
            {busy ? "Locking…" : "Lock it"}
          </button>
        </form>
      </div>
    </div>
  );
}

